import { useMemo } from 'react'
import { create } from 'zustand'
import type { StreamingToolCall, StreamingTurn } from '../../../hooks/queries/useChatStream'

/**
 * What one assistant turn did, as far as the client ever gets to know it.
 *
 * Thinking and tool events exist ONLY on the stream: `message.blocks[]` carries
 * `approval_request` and nothing else, and no event carries a `message_id`. So
 * once a turn resolves and the persisted row replaces the live bubble, this is
 * the only copy there is.
 *
 * Session-scoped by construction — a reload loses every trace and those
 * messages render with no activity row. Making it durable needs the service to
 * persist thinking/tool blocks the way it already persists `approval_request`;
 * that is an open backend ask, not something to fake here by writing stream
 * state into the query cache.
 */
export interface TurnTrace {
  turnId: string
  thinking?: string
  tools: StreamingToolCall[]
  /** Absent when unmeasurable or implausibly short — see below. */
  durationMs?: number
}

/** Per chat. Old traces are evicted rather than held for a whole session. */
export const MAX_TRACES_PER_CHAT = 50

/**
 * Below this, the measurement is not believable and is dropped.
 *
 * `startedAt`/`endedAt` are OUR clocks, and backfill replays a completed turn
 * through the same fold in a single batch — so a replayed turn measures a few
 * milliseconds regardless of how long it actually took. A genuinely sub-second
 * turn has nothing worth reporting either, so one rule covers both.
 */
export const MIN_REPORTABLE_DURATION_MS = 1000

export function traceFromTurn(turn: StreamingTurn): TurnTrace {
  const measured = turn.endedAt === undefined ? undefined : turn.endedAt - turn.startedAt
  return {
    turnId: turn.turnId,
    thinking: turn.thinking,
    tools: turn.tools,
    durationMs:
      measured !== undefined && measured >= MIN_REPORTABLE_DURATION_MS ? measured : undefined,
  }
}

interface ChatTraceState {
  /** chatId -> messageId -> trace */
  byChat: Record<string, Record<string, TurnTrace>>
  /** chatId -> messageIds in commit order, oldest first. Drives eviction. */
  order: Record<string, string[]>
  commitTrace: (chatId: string, messageId: string, trace: TurnTrace) => void
  clearChat: (chatId: string) => void
}

export const useChatTraceStore = create<ChatTraceState>((set) => ({
  byChat: {},
  order: {},

  commitTrace: (chatId, messageId, trace) =>
    set((state) => {
      const traces = { ...(state.byChat[chatId] ?? {}) }
      // Re-committing an id must not consume a second slot, or a re-render
      // storm would evict live traces to make room for duplicates of one.
      const order = (state.order[chatId] ?? []).filter((id) => id !== messageId)

      traces[messageId] = trace
      order.push(messageId)

      while (order.length > MAX_TRACES_PER_CHAT) {
        const evicted = order.shift()
        if (evicted !== undefined) delete traces[evicted]
      }

      return {
        byChat: { ...state.byChat, [chatId]: traces },
        order: { ...state.order, [chatId]: order },
      }
    }),

  clearChat: (chatId) =>
    set((state) => {
      const byChat = { ...state.byChat }
      const order = { ...state.order }
      delete byChat[chatId]
      delete order[chatId]
      return { byChat, order }
    }),
}))

/**
 * The trace for one message, if we hold one.
 *
 * Subscribes to the chat's slice rather than to the whole store, so committing
 * a trace in one chat does not re-render every message of another. The `useMemo`
 * keeps the returned value referentially stable while the slice is unchanged.
 */
export function useTurnTrace(
  chatId: string | null | undefined,
  messageId: string,
): TurnTrace | undefined {
  const traces = useChatTraceStore((state) => (chatId ? state.byChat[chatId] : undefined))
  return useMemo(() => traces?.[messageId], [traces, messageId])
}
